import React, { useState, useEffect } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Briefcase,
  User,
  Building,
  CheckSquare,
  Zap,
  PlusCircle,
  RefreshCw,
  Trash2,
} from "lucide-react";
import type { TriggerItem, TriggerEvent } from "./types";
import { TRIGGER_ITEM_LABELS, TRIGGER_EVENT_LABELS } from "./constants";

interface TriggerSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  item: TriggerItem;
  evento: TriggerEvent;
  primeiraAtividade: boolean;
  onSave: (data: {
    gatilho_item: TriggerItem;
    gatilho_evento: TriggerEvent;
    primeira_atividade_negocio: boolean;
  }) => void;
}

const ITEM_ICONS: Record<TriggerItem, React.ElementType> = {
  negocio: Briefcase,
  pessoa: User,
  organizacao: Building,
  atividade: CheckSquare,
};

const EVENT_ICONS: Record<TriggerEvent, React.ElementType> = {
  criado: PlusCircle,
  atualizado: RefreshCw,
  excluido: Trash2,
};

export const TriggerSheet: React.FC<TriggerSheetProps> = ({
  open,
  onOpenChange,
  item: initialItem,
  evento: initialEvento,
  primeiraAtividade: initialPrimeira,
  onSave,
}) => {
  const [selectedItem, setSelectedItem] = useState<TriggerItem>(initialItem);
  const [selectedEvento, setSelectedEvento] =
    useState<TriggerEvent>(initialEvento);
  const [primeiraAtividade, setPrimeiraAtividade] =
    useState<boolean>(initialPrimeira);

  useEffect(() => {
    setSelectedItem(initialItem);
    setSelectedEvento(initialEvento);
    setPrimeiraAtividade(initialPrimeira);
  }, [initialItem, initialEvento, initialPrimeira, open]);

  const handleSave = () => {
    onSave({
      gatilho_item: selectedItem,
      gatilho_evento: selectedEvento,
      primeira_atividade_negocio:
        selectedItem === "atividade" ? primeiraAtividade : false,
    });
    onOpenChange(false);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-lg flex flex-col h-full overflow-y-auto gap-0">
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-border/60">
          <div className="flex items-center gap-2 text-primary font-semibold text-xs tracking-wider uppercase">
            <Zap className="w-4 h-4 text-amber-500" />
            Configuração do Gatilho
          </div>
          <SheetTitle className="text-xl font-bold">
            Quando este fluxo deve disparar?
          </SheetTitle>
          <SheetDescription className="text-sm text-muted-foreground">
            Escolha o registro do CRM e o evento que dará início à execução da
            automação.
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 px-6 py-5 space-y-6">
          {/* 1. SELEÇÃO DO REGISTRO */}
          <div className="space-y-3">
            <Label className="text-xs font-semibold text-foreground uppercase tracking-wider">
              1. Selecione o Tipo de Registro
            </Label>
            <div className="grid grid-cols-2 gap-2.5">
              {(
                [
                  "negocio",
                  "pessoa",
                  "organizacao",
                  "atividade",
                ] as TriggerItem[]
              ).map((it) => {
                const Icon = ITEM_ICONS[it];
                const meta = TRIGGER_ITEM_LABELS[it];
                const isSelected = selectedItem === it;
                return (
                  <button
                    key={it}
                    type="button"
                    onClick={() => setSelectedItem(it)}
                    className={`flex flex-col items-start p-3.5 rounded-xl border text-left transition-all cursor-pointer ${
                      isSelected
                        ? "border-primary bg-primary/10 shadow-sm ring-1 ring-primary"
                        : "border-border/70 hover:border-border hover:bg-muted/40"
                    }`}
                  >
                    <div
                      className={`p-2 rounded-lg mb-2 ${
                        isSelected
                          ? "bg-primary text-primary-foreground"
                          : "bg-muted text-muted-foreground"
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                    </div>
                    <span className="font-semibold text-sm text-foreground">
                      {meta.label}
                    </span>
                    <span className="text-[11px] text-muted-foreground line-clamp-2 mt-0.5">
                      {meta.desc}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* 2. SELEÇÃO DO EVENTO */}
          <div className="space-y-3">
            <Label className="text-xs font-semibold text-foreground uppercase tracking-wider">
              2. Quando o evento ocorrer
            </Label>
            <div className="grid grid-cols-3 gap-2">
              {(["criado", "atualizado", "excluido"] as TriggerEvent[]).map(
                (ev) => {
                  const Icon = EVENT_ICONS[ev];
                  const meta = TRIGGER_EVENT_LABELS[ev];
                  const isSelected = selectedEvento === ev;
                  return (
                    <button
                      key={ev}
                      type="button"
                      onClick={() => setSelectedEvento(ev)}
                      className={`flex flex-col items-center justify-center p-3 rounded-xl border text-center transition-all cursor-pointer ${
                        isSelected
                          ? "border-primary bg-primary/10 shadow-sm ring-1 ring-primary"
                          : "border-border/70 hover:border-border hover:bg-muted/40"
                      }`}
                    >
                      <Icon
                        className={`w-4 h-4 mb-1.5 ${isSelected ? "text-primary" : "text-muted-foreground"}`}
                      />
                      <span className="font-semibold text-xs text-foreground">
                        {meta.label}
                      </span>
                    </button>
                  );
                },
              )}
            </div>
          </div>

          {/* 3. OPÇÃO CONDICIONAL: PRIMEIRA ATIVIDADE DO NEGÓCIO */}
          {selectedItem === "atividade" && (
            <div className="p-4 rounded-xl border border-amber-500/30 bg-amber-500/5 space-y-2">
              <div className="flex items-start gap-3">
                <Checkbox
                  id="primeira_atividade"
                  checked={primeiraAtividade}
                  onCheckedChange={(c) => setPrimeiraAtividade(!!c)}
                  className="mt-0.5"
                />
                <div className="space-y-0.5">
                  <label
                    htmlFor="primeira_atividade"
                    className="text-xs font-semibold text-foreground cursor-pointer"
                  >
                    Disparar apenas na primeira atividade do negócio
                  </label>
                  <p className="text-[11px] text-muted-foreground">
                    Se marcado, a automação só executará se o negócio ainda não
                    tiver nenhuma outra atividade registrada no histórico.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* RESUMO DO GATILHO */}
          <div className="p-3.5 rounded-lg border border-border/60 bg-muted/30 flex items-center gap-3">
            <div className="p-2 rounded-full bg-primary/10 text-primary">
              <Zap className="w-4 h-4" />
            </div>
            <div className="text-xs">
              <span className="text-muted-foreground">Resumo: </span>
              <span className="font-medium text-foreground">
                Disparar quando{" "}
                <strong className="text-primary font-semibold">
                  {TRIGGER_ITEM_LABELS[selectedItem].label}
                </strong>{" "}
                for{" "}
                <strong className="text-primary font-semibold">
                  {TRIGGER_EVENT_LABELS[selectedEvento].label.toLowerCase()}
                </strong>
                {selectedItem === "atividade" && primeiraAtividade
                  ? " (apenas se for a 1ª atividade)"
                  : ""}
                .
              </span>
            </div>
          </div>
        </div>

        <SheetFooter className="px-6 py-4 border-t border-border/60 flex flex-row items-center justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSave} className="gap-1.5 shadow-sm">
            Salvar Gatilho
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};
